import { useSignUpForm } from '@/forms/sign-up.form';
import { emailSchema } from '@/validation/email.schema';
import {
  Box,
  Button,
  FormHelperText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import useSignIn from 'react-auth-kit/hooks/useSignIn';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContainer } from '../components/auth-container.component';

const inputSx = {
  '& .MuiFilledInput-root': {
    bgcolor: 'white',
    borderRadius: 2,
    '&:before, &:after': { display: 'none' },
    '&:hover': { bgcolor: 'white' },
    '&.Mui-focused': { bgcolor: 'white' },
  },
};

const disabledInputSx = {
  '& .MuiFilledInput-root': {
    bgcolor: 'rgba(255,255,255,0.6)',
    borderRadius: 2,
    '&:before, &:after': { display: 'none' },
  },
};

export function AuthSignUpPage() {
  const navigate = useNavigate();
  const signIn = useSignIn();
  const [searchParams] = useSearchParams();
  const email = String(searchParams.get('email'));
  const code = String(searchParams.get('code'));

  const {
    register,
    onSubmit,
    setValue,
    setError,
    formState: { errors: formErrors },
  } = useSignUpForm({
    onSuccess: (response) => {
      if (response.accessToken) {
        signIn({
          auth: {
            token: response.accessToken,
            type: 'Bearer',
          },
        });
        navigate(`/?success`);
      }
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          navigate(
            `/auth/code?email=${encodeURIComponent(email)}&error=invalid_code`,
          );
        } else if (error.response?.status === 409) {
          setError('email', {
            message: error.response?.data.message,
          });
        } else if (error.response?.status === 404) {
          navigate(`/auth?error=invalid_code`);
        }
      }
    },
  });

  useEffect(() => {
    if (!emailSchema.safeParse(email).success) {
      console.error('Invalid email');
      navigate('/auth');
      return;
    }
    setValue('email', email);
    if (code?.length !== 6) {
      console.error('Invalid code');
      navigate('/auth');
      return;
    }
    setValue('code', code);
  }, [email, code, navigate]);

  const handleBackClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    navigate('/auth');
  };

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          color: '#02344C',
          fontWeight: 700,
          mb: 0.75,
          textAlign: 'center',
          letterSpacing: '-0.01em',
        }}
      >
        Registrieren
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 1,
          textAlign: 'center',
          maxWidth: 340,
          lineHeight: 1.6,
        }}
      >
        Geben Sie Ihr gewünschtes Passwort ein, um fortzufahren.
      </Typography>
      <AuthContainer component="form" onSubmit={onSubmit}>
        <Tooltip
          title="Um die E-Mail-Adresse zu ändern, gehen Sie bitte zurück zur vorherigen Seite."
          arrow
          placement="top"
        >
          <TextField
            id="email"
            label="E-Mail Adresse"
            type="email"
            variant="filled"
            {...register('email')}
            fullWidth
            required
            value={email}
            disabled
            sx={disabledInputSx}
          />
        </Tooltip>
        <TextField
          id="password"
          label="Passwort"
          type="password"
          variant="filled"
          {...register('password')}
          fullWidth
          required
          error={!!formErrors.password}
          helperText={formErrors.password?.message}
          sx={inputSx}
        />
        <TextField
          id="password-confirm"
          label="Passwort wiederholen"
          type="password"
          variant="filled"
          {...register('confirmPassword')}
          fullWidth
          required
          error={!!formErrors.confirmPassword}
          helperText={formErrors.confirmPassword?.message}
          sx={inputSx}
        />
        {!!formErrors.code && (
          <FormHelperText error>
            {formErrors.code?.message}
          </FormHelperText>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%',
            gap: 1.5,
            mt: 1,
          }}
        >
          <Button
            onClick={handleBackClick}
            fullWidth
            variant="outlined"
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Zurück
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            Weiter
          </Button>
        </Box>
      </AuthContainer>
    </>
  );
}
